import React, { useState, useEffect, createContext } from 'react';
import { ThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import { Box, CircularProgress } from '@material-ui/core';
import theme from '../theme';
import DriverLogin from './pages/DriverLogin';
import DriverRoutes from './pages/DriverRoutes';
import DriverRouteDetail from './pages/DriverRouteDetail';
import DriverOrderDetail from './pages/DriverOrderDetail';

const API = '/planner-api';
const STORAGE_KEY = 'driver_token';

export const DriverAuthContext = createContext({ token: null, driver: null, logout: () => {} });

// Very small hash-based router: #/driver, #/driver/routes, #/driver/routes/:id, #/driver/orders/:id
function parseRoute(hash) {
  const h = (hash || '').replace(/^#?\/?/, '');
  const parts = h.split('/').filter(Boolean); // ['driver', ...]
  if (parts[0] !== 'driver') return { page: 'root' };
  if (parts.length === 1) return { page: 'home' };
  if (parts[1] === 'routes' && !parts[2]) return { page: 'routes' };
  if (parts[1] === 'routes' && parts[2]) return { page: 'route-detail', routeId: parts[2] };
  if (parts[1] === 'orders' && parts[2]) return { page: 'order-detail', orderId: parts[2] };
  return { page: 'home' };
}

function navigate(path) {
  window.location.hash = path; // e.g. '/driver/routes'
}

export default function DriverApp() {
  const [token, setToken] = useState(null);
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [route, setRoute] = useState(parseRoute(window.location.hash));

  useEffect(() => {
    function onHash() { setRoute(parseRoute(window.location.hash)); }
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setToken(data.token);
        setDriver(data.driver);
      } catch {}
    }
    setLoading(false);
  }, []);

  function handleLogin(data) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setToken(data.token);
    setDriver(data.driver);
    navigate('/driver/routes');
  }

  function logout() {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setDriver(null);
    navigate('/driver');
  }

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8fafc' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (!token) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <DriverLogin onLogin={handleLogin} api={API} />
      </ThemeProvider>
    );
  }

  const authCtx = { token, driver, logout, api: API };

  let page;
  if (route.page === 'route-detail') page = <DriverRouteDetail routeId={route.routeId} />;
  else if (route.page === 'order-detail') page = <DriverOrderDetail orderId={route.orderId} />;
  else page = <DriverRoutes />;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <DriverAuthContext.Provider value={authCtx}>
        {page}
      </DriverAuthContext.Provider>
    </ThemeProvider>
  );
}

export { navigate };
